'use client'
import React, {useRef, useState, useEffect, Fragment} from "react";
import {FormikHelpers, FormikProps, FieldProps} from 'formik';
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import {BootstrapInput} from "@/ui/bootstrap-input/bootstrap-input";
import {styled} from '@mui/material/styles';
import {Field, Form, Formik} from 'formik'
import {ToastContainer, toast} from 'react-toastify';
import ReCAPTCHA from "react-google-recaptcha";
import {businessDetails, BUSINESS_NAME} from '@/content';
import DialogActions from "@mui/material/DialogActions";
import * as Yup from 'yup'
import axios from 'axios'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import InputLabel from "@mui/material/InputLabel";
import {initialValues} from './helper'
import styles from '@/ui/contact-form/contact-form.module.scss';
import {Box} from "@mui/material";
import clsx from "clsx";
import Link from "next/link";

const tooShort = "Value must be greater than 2 characters"
const tooLong = "Value must be less than 50 characters"


type TempValuesType = {
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    phoneNumber: string;
    enquiry: string;
    captcha: string; // Updated from `string | undefined` to `string`
    dateOfWedding?: Date | null;
};

const SignupSchema = Yup.object().shape({
    firstName: Yup.string()
        .min(2, tooShort)
        .max(50, tooLong)
        .required('Required'),
    lastName: Yup.string()
        .min(2, tooShort)
        .max(50, tooLong)
        .required('Required'),
    email: Yup.string()
        .email('Invalid email')
        .required('Required'),
    confirmEmail: Yup.string()
        .email('Invalid email')
        .oneOf([Yup.ref('email'), ''], "Email and confirm email must be equal")
        .required('Required')
    ,
    phoneNumber: Yup.string()
        .min(2, tooShort)
        .max(50, tooLong)
        .required('Required'),
    enquiry: Yup.string()
        .min(2, tooShort)
        .max(50, tooLong)
        .required('Required'),
});

type ContactFormProps = {
    buttonMessage?: string;
    formOnly?: boolean;
    buttonVariant?: 'text' | 'contained' | 'outlined';
}


const ContactForm = ({buttonMessage = 'Enquire Now', formOnly = false, buttonVariant = 'text'}: ContactFormProps) => {

    const {captchaKeys} = businessDetails;

    const [open, setOpen] = React.useState(false);
    const bagRef = useRef<FormikProps<TempValuesType> | null>(null);
    const [formSubmitted, setFormSubmitted] = useState(false)
    const [recaptchaError, setRecaptchaError] = useState(false)
    const [, setPostForm] = useState(false)
    const [reCaptchaVal, setReCaptchaVal] = useState<string | boolean>(false);
    const [emailIsSending, setEmailIsSending] = useState(false)
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);


    const sendEmail = ({values, resetForm}: {
        values: TempValuesType,
        resetForm: FormikHelpers<TempValuesType>['resetForm']
    }) => {

        const contactURL = process.env.NODE_ENV === 'development' ?
            'http://localhost/flx/api/contact/'
            : '../../api/contact/'

        const id = toast.loading("Sending enquiry", {position: "bottom-center"})

        try {
            axios
                .request({
                    method: "post",
                    url: contactURL,
                    data: values,
                })
                .then((response) => {
                    console.log('response', response)
                    toast.update(id, {
                        render: "Enquiry sent!", type: "success", isLoading: false, hideProgressBar: false,
                        autoClose: 3000,
                        progress: null,
                    })
                    if (recaptchaRef && recaptchaRef.current) recaptchaRef.current.reset()
                    setEmailIsSending(false)
                    setPostForm(false)
                    resetForm()
                    setStartDate(null)
                    setTimeout(() => handleClose(), 3100)

                }).catch(function (error) {

                const errorMessage = error?.response?.data?.data?.[0] || "Error sending enquiry, please try again later."

                if (recaptchaRef && recaptchaRef.current) recaptchaRef.current.reset()
                setEmailIsSending(false)
                setPostForm(false)
                setStartDate(null)
                toast.update(id, {
                    render: errorMessage,
                    type: "error",
                    hideProgressBar: false,
                    autoClose: 3000,
                    progress: null,
                    isLoading: false
                })

            });
        } catch (error) {
            console.log('error here', error)
        }


    };

    // todo wire up after
    // const handleClickOpen = () => () => {
    //     setOpen(true);
    // };

    const handleClose = () => {
        setOpen(false);
    };

    const handleModalClose = (event: React.SyntheticEvent, reason: string) => {
        if (reason && reason === "backdropClick")
            return;
        handleClose();
    }


    const BootstrapDialogTitle = styled(Typography)(({theme}) => ({
        fontSize: '20px',
        padding: theme.spacing(0, 2),
    }));

    //hook handles the captcha events
    useEffect(
        () => {
            if (!formSubmitted) return
            //stops it initially
            if (!reCaptchaVal || reCaptchaVal === '') {
                setRecaptchaError(true)
                setFormSubmitted(false)
                return
            }
            setEmailIsSending(true)
            setRecaptchaError(false)
            setPostForm(true)
            setFormSubmitted(false)
            setOpen(false)
        }, [formSubmitted, reCaptchaVal]
    )

    const submitButton = <Button onClick={
        () => {
            if (bagRef.current) {
                bagRef.current?.handleSubmit();
            }
            // notify();
        }
    }
                                 variant="contained"
                                 disabled={emailIsSending}
                                 endIcon={emailIsSending && <HourglassBottomIcon/>}
                                 fullWidth={formOnly}
    >Submit</Button>


    const formContent = <>
        <ToastContainer limit={1} autoClose={3000} closeOnClick/>
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validateOnBlur={true}
            validateOnChange={true}
            validationSchema={SignupSchema}
            innerRef={bagRef}
            onSubmit={(values, {resetForm}) => {

                //custom recaptcha error
                if (reCaptchaVal === '' || !reCaptchaVal) {
                    setRecaptchaError(true)
                } else {
                    setRecaptchaError(false)
                    if (bagRef.current) {
                        bagRef.current.setFieldValue("captcha", reCaptchaVal);
                    }
                    // const formVals = {...values, captcha: reCaptchaVal, dateOfWedding: startDate}
                    const formVals: TempValuesType = {
                        ...values,
                        captcha: typeof reCaptchaVal === 'string' ? reCaptchaVal : '',
                        dateOfWedding: startDate,
                    };
                    sendEmail({values: formVals, resetForm})
                    setFormSubmitted(true)
                }
            }}
        >
            {({errors, touched}) => (
                <Form>
                    <Box className={styles.contactForm}>
                        <div className="textDiv">
                            <InputLabel shrink htmlFor="firstName">
                                First name*
                            </InputLabel>
                            <Field name="firstName">
                                {({field}: FieldProps) => (
                                    <BootstrapInput
                                        fullWidth
                                        color="primary"
                                        id="firstName"
                                        // label="First name"
                                        // variant="standard"
                                        disabled={emailIsSending}
                                        {...field}
                                    />
                                )}
                            </Field>
                            {/*<Field name="firstName" placeholder="First name" disabled={emailIsSending}/>*/}
                            {errors.firstName && touched.firstName ? (
                                <div className={styles.errorDiv}>{errors.firstName}</div>
                            ) : null}
                        </div>
                        <div className="textDiv">
                            <InputLabel shrink htmlFor="firstName">
                                Last name*
                            </InputLabel>
                            <Field name="lastName">
                                {({field}: FieldProps) => (
                                    <BootstrapInput
                                        fullWidth
                                        color="primary"
                                        id="lastName"
                                        // label="Last name"
                                        // variant="standard"
                                        disabled={emailIsSending}
                                        {...field}
                                    />
                                )}
                            </Field>
                            {errors.lastName && touched.lastName ? (
                                <div className={styles.errorDiv}>{errors.lastName}</div>
                            ) : null}
                        </div>
                        <div className={styles.textDiv}>
                            <InputLabel shrink htmlFor="firstName">
                                Email*
                            </InputLabel>
                            <Field name="email" type="email">
                                {({field}: FieldProps) => (
                                    <BootstrapInput
                                        fullWidth
                                        color="primary"
                                        id="email"
                                        // label="Email*"
                                        // variant="standard"
                                        disabled={emailIsSending}
                                        {...field}
                                    />
                                )}
                            </Field>
                            {errors.email && touched.email ? (
                                <div className={styles.errorDiv}>{errors.email}</div>
                            ) : null}
                        </div>
                        <div className={styles.textDiv}>
                            <InputLabel shrink htmlFor="firstName">
                                Confirm email*
                            </InputLabel>
                            <Field name="confirmEmail" type="email">
                                {({field}: FieldProps) => (
                                    <BootstrapInput
                                        fullWidth
                                        color="primary"
                                        id="confirmEmail"
                                        // label="Confirm email*"
                                        // variant="standard"
                                        disabled={emailIsSending}
                                        {...field}
                                    />
                                )}
                            </Field>
                            {errors.confirmEmail && touched.confirmEmail ? (
                                <div className={styles.errorDiv}>{errors.confirmEmail}</div>
                            ) : null}
                        </div>
                        <div className={styles.textDiv}>
                            <InputLabel shrink htmlFor="firstName">
                                Phone number*
                            </InputLabel>
                            <Field name="phoneNumber">
                                {({field}: FieldProps) => (
                                    <BootstrapInput
                                        fullWidth
                                        color="primary"
                                        id="phoneNumber"
                                        // label="Phone number*"
                                        // variant="standard"
                                        disabled={emailIsSending}
                                        {...field}
                                    />
                                )}
                            </Field>
                            {errors.phoneNumber && touched.phoneNumber ? (
                                <div className={styles.errorDiv}>{errors.phoneNumber}</div>
                            ) : null}
                        </div>

                        <div className={styles.doubleSpan}>
                            <InputLabel shrink htmlFor="firstName">
                                Enquiry*
                            </InputLabel>
                            <Field name="enquiry">
                                {({field}: FieldProps) => (
                                    <BootstrapInput
                                        fullWidth
                                        color="primary"
                                        id="enquiry"
                                        // label="Message"
                                        // variant="standard"
                                        disabled={emailIsSending}
                                        {...field}
                                        multiline
                                        minRows={5}
                                    />
                                )}
                            </Field>

                            {errors.enquiry && touched.enquiry ? (
                                <div className={styles.errorDiv}>{errors.enquiry}</div>
                            ) : null}
                        </div>

                        <div className={clsx(styles.textDiv, styles.doubleSpan)}>
                            {!emailIsSending && (
                                <Fragment>
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={
                                            process.env.NODE_ENV === 'development'
                                                ? captchaKeys?.dev || ''
                                                : captchaKeys?.prod || ''
                                        }
                                        onChange={param => setReCaptchaVal(param ?? false)}
                                    />
                                    {recaptchaError && (
                                        <div className={styles.errorDiv}>Please satisfy reCAPTCHA requirement</div>
                                    )}
                                </Fragment>
                            )}
                        </div>
                    </Box>
                </Form>
            )}
        </Formik>
        {formOnly && submitButton}
    </>

    if (formOnly) return <>
        {formContent}
    </>

    return (<>

        {/*todo wire this up when the form is ready*/}
        {/*<Button onClick={handleClickOpen} variant={buttonVariant}>{buttonMessage}</Button>*/}
        <Button href='/contact' component={Link}  variant={buttonVariant}>{buttonMessage}</Button>
        <Dialog
            open={open}
            scroll='paper'
            fullWidth
            disableEscapeKeyDown
            onClose={handleModalClose}
        >
            <BootstrapDialogTitle
                id="scroll-dialog-title"
                variant='h6'
                // component='h6'
                align="center"
            >{BUSINESS_NAME} Enquiry
            </BootstrapDialogTitle>
            <DialogContent dividers>
                {formContent}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                {submitButton}
            </DialogActions>
        </Dialog>
    </>)
}

export default ContactForm;
